import { useMemo, type CSSProperties } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { MembershipsSearchParams } from "../components/memberships/api";
import { MembershipsKpis } from "../components/memberships/MembershipsKpis";
import { MembershipsStats } from "../components/memberships/MembershipsStats";
import { previousWindow } from "../components/memberships/previousWindow";
import { ComparePeriodPicker, type PickerPatch } from "../components/reports/ComparePeriodPicker";
import {
  anchorForKind,
  resolveComparePeriod,
  type CompareKind,
} from "../components/reports/resolveComparePeriod";

const searchSchema = z.object({
  status: z.enum(["active", "inactive", "expired", "upcoming", "all"]).optional(),
  include_single_visit: z.boolean().optional(),
  show_all_terms: z.boolean().optional(),
  // KPI period axis (current window). Previous window is derived.
  kind: z.enum(["month", "quarter", "year", "custom"]).optional(),
  anchor: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
});

type StatsSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_auth/memberships/stats")({
  staticData: { titleKey: "nav.memberships", breadcrumbKey: "breadcrumb.operate", breadcrumbN: 6 },
  validateSearch: searchSchema,
  component: MembershipsStatsPage,
});

const STATUSES = ["all", "active", "inactive", "expired", "upcoming"] as const;

function MembershipsStatsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const status = search.status ?? "active";
  const today = useMemo(() => new Date(), []);

  const kind: CompareKind = search.kind ?? "month";
  const anchor = search.anchor ?? anchorForKind(kind, today);
  const current = resolveComparePeriod(kind, anchor, search.from, search.to, today);
  const previous = previousWindow(kind, anchor, current, today);

  function update(patch: Partial<StatsSearch>) {
    void navigate({ to: "/memberships/stats", search: (prev) => ({ ...prev, ...patch }) as never });
  }

  function patchPeriod(p: PickerPatch) {
    update({
      ...(p.kind !== undefined ? { kind: p.kind } : {}),
      ...(p.anchor !== undefined ? { anchor: p.anchor } : {}),
      ...(p.from !== undefined ? { from: p.from } : {}),
      ...(p.to !== undefined ? { to: p.to } : {}),
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <ComparePeriodPicker
        labelKey="memberships.kpi.period"
        kind={kind}
        anchor={anchor}
        from={search.from ?? current.from}
        to={search.to ?? current.to}
        range={current}
        today={today}
        onChange={patchPeriod}
      />
      <MembershipsKpis
        current={current}
        previous={previous}
        kind={kind}
        includeSingleVisit={search.include_single_visit}
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={seg}>
            {STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => update({ status: s })}
                style={{ ...segBtn, ...(status === s ? segOn : {}) }}
              >
                {t(`memberships.filter.status.${s}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            style={{ ...ghost, ...(search.show_all_terms ? ghostOn : {}) }}
            title={t("memberships.filter.showAllTermsHint")}
            onClick={() => update({ show_all_terms: search.show_all_terms ? undefined : true })}
          >
            {t("memberships.filter.showAllTerms")}
          </button>
          <button
            type="button"
            style={{ ...ghost, ...(search.include_single_visit ? ghostOn : {}) }}
            onClick={() =>
              update({ include_single_visit: search.include_single_visit ? undefined : true })
            }
          >
            {t("memberships.filter.includeSingleVisit")}
          </button>
        </div>
        <MembershipsStats search={search as MembershipsSearchParams} />
      </div>
    </div>
  );
}

const seg: CSSProperties = {
  display: "inline-flex",
  gap: 4,
  padding: 4,
  borderRadius: 999,
  background: "var(--bg-inset)",
};
const segBtn: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "4px 10px",
  borderRadius: 999,
  border: "1px solid transparent",
  background: "transparent",
  color: "var(--fg-mute)",
  cursor: "pointer",
};
const segOn: CSSProperties = {
  background: "var(--bg)",
  color: "var(--fg)",
  border: "1px solid var(--line-soft)",
};
// See MembershipsFilters.ghostOn: segOn is invisible on the page background.
const ghostOn: CSSProperties = {
  background: "var(--fg)",
  color: "var(--bg)",
  border: "1px solid var(--fg)",
};
const ghost: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  padding: "6px 9px",
  border: "1px solid var(--line)",
  borderRadius: 6,
  background: "transparent",
  color: "var(--fg)",
  cursor: "pointer",
};
